import { http } from "@/lib/http";
import type { AuthenticatedUser } from "@/stores/authStore";

import type {
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  SsoLoginResponse,
} from "./types";

// withCredentials: login/register responses SET the httpOnly `omni_refresh`
// cookie and logout must SEND it so the server can revoke it.
export async function login(payload: LoginRequest): Promise<LoginResponse> {
  const { data } = await http.post<LoginResponse>("/auth/login", payload, {
    withCredentials: true,
  });
  return data;
}

export async function register(
  payload: RegisterRequest,
): Promise<LoginResponse> {
  const { data } = await http.post<LoginResponse>("/auth/register", payload, {
    withCredentials: true,
  });
  return data;
}

export async function fetchMe(): Promise<AuthenticatedUser> {
  const { data } = await http.get<AuthenticatedUser>("/auth/me");
  return data;
}

/** The caller's own effective permissions (Phase C) — for hiding dead UI. */
export interface MyPermissions {
  role: string;
  is_admin: boolean;
  permissions: Record<string, string[]>;
  mfa_enabled: boolean;
  mfa_enrollment_required: boolean;
  email_verified: boolean;
}

// --- Password reset + email verification (Phase B1/B2) ------------------- //

export async function forgotPassword(
  email: string,
  captchaToken?: string,
): Promise<void> {
  await http.post("/auth/forgot-password", { email, captcha_token: captchaToken });
}

export async function resetPassword(
  token: string,
  new_password: string,
): Promise<void> {
  await http.post("/auth/reset-password", { token, new_password });
}

export async function verifyEmail(token: string): Promise<void> {
  await http.post("/auth/verify-email", { token });
}

export async function resendVerification(): Promise<void> {
  await http.post("/auth/resend-verification");
}

export async function fetchMyPermissions(): Promise<MyPermissions> {
  const { data } = await http.get<MyPermissions>("/auth/me/permissions");
  return data;
}

export async function logout(): Promise<void> {
  await http.post("/auth/logout", null, { withCredentials: true });
}

export async function ssoLogin(): Promise<SsoLoginResponse> {
  const { data } = await http.get<SsoLoginResponse>("/auth/sso/login");
  return data;
}

/** Finish a company sign-in (SASE edition, SASE-30): the page at the
 *  identity provider's redirect hands back the `code` and `state` it was
 *  given. withCredentials: the answer sets the refresh cookie and the request
 *  must carry the cookie that binds the sign-in to this browser. */
export async function ssoCallback(code: string, state: string): Promise<LoginResponse> {
  const { data } = await http.get<LoginResponse>("/auth/sso/callback", {
    params: { code, state },
    withCredentials: true,
  });
  return data;
}
